import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ConversationPage, ConversationSummary } from '../types';
import { listSessions } from '../api/client';
import { HISTORY_PAGE_SIZE } from '../constants';
import {
  CurrentBadge,
  EmptyState,
  HistoryList,
  HistoryMeta,
  HistoryPreview,
  HistoryRow,
  HistoryRowTop,
  LoadMoreButton,
  LoadMoreError,
  PanelBody,
} from '../styles';

interface Props {
  onSelect: (sessionId: string) => void;
  // Highlighted in the list, so the user can tell which thread they are already in.
  activeSessionId?: string | null;
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

function ago(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'} ago`;
}

// "3 days ago" reads better than a timestamp in a list whose whole purpose is "which
// of these is the one I want". Both arguments are epoch *seconds* -- the service
// emits its timestamps in seconds, not milliseconds.
//
// Returns the bare phrase, lowercase, so the caller can label which timestamp it is
// describing; a bare date here would be ambiguous between when a conversation was
// started and when it was last used.
export function formatRelative(epochSeconds: number | null, nowSeconds: number): string {
  if (!epochSeconds) {
    return '';
  }
  // Clamped: a few seconds of clock skew between the service and the browser should
  // not render a conversation as being from the future.
  const elapsed = Math.max(0, nowSeconds - epochSeconds);
  if (elapsed < MINUTE) {
    return 'just now';
  }
  if (elapsed < HOUR) {
    return ago(Math.floor(elapsed / MINUTE), 'minute');
  }
  if (elapsed < DAY) {
    return ago(Math.floor(elapsed / HOUR), 'hour');
  }
  if (elapsed < MONTH) {
    return ago(Math.floor(elapsed / DAY), 'day');
  }
  if (elapsed < YEAR) {
    return ago(Math.floor(elapsed / MONTH), 'month');
  }
  return ago(Math.floor(elapsed / YEAR), 'year');
}

export function HistoryPanel({ onSelect, activeSessionId }: Props) {
  const [conversations, setConversations] = useState<ConversationSummary[] | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Separate from `error`: a failed "load more" must not blow away the rows already
  // fetched, so it renders as an inline retry rather than replacing the whole list.
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  // `loadMore` is a callback invoked from a click, not tied to a single effect run
  // (unlike the initial fetch's `active` flag), so it needs its own unmount guard:
  // toggling back to the chat panel mid-fetch must not set state on a gone component.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Rows with no messages are sessions the composer warmed and nobody used -- real
  // to the service, meaningless here. Filtering client-side means a page can render
  // slightly short; that is fine, and cheaper than the service filtering server-side,
  // which would have to over-fetch to keep pages a fixed size.
  const appendPage = useCallback((page: ConversationPage, replace: boolean) => {
    const rows = page.conversations.filter((row) => row.messageCount > 0);
    setConversations((previous) => (replace || previous === null ? rows : [...previous, ...rows]));
    setNextCursor(page.nextCursor);
  }, []);

  // Fetched on open rather than kept in sync: the list is a point-in-time view, and
  // reopening the panel is the natural way to refresh it.
  useEffect(() => {
    let active = true;
    listSessions(HISTORY_PAGE_SIZE)
      .then((page) => {
        if (active) {
          appendPage(page, true);
        }
      })
      .catch((err: Error) => {
        if (active) {
          setError(err.message);
        }
      });
    return () => {
      active = false;
    };
  }, [appendPage]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || isLoadingMore) {
      return;
    }
    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await listSessions(HISTORY_PAGE_SIZE, nextCursor);
      if (mountedRef.current) {
        appendPage(page, false);
      }
    } catch (err) {
      if (mountedRef.current) {
        setLoadMoreError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (mountedRef.current) {
        setIsLoadingMore(false);
      }
    }
  }, [nextCursor, isLoadingMore, appendPage]);

  if (error) {
    return (
      <PanelBody>
        <EmptyState>Could not load your chats. {error}</EmptyState>
      </PanelBody>
    );
  }

  if (conversations === null) {
    return (
      <PanelBody>
        <EmptyState>Loading your chats…</EmptyState>
      </PanelBody>
    );
  }

  // A page can come back empty after filtering out warmed-but-unused sessions while
  // still having more pages behind it -- only call the list truly empty once there
  // is no further cursor to load.
  if (conversations.length === 0 && !nextCursor) {
    return (
      <PanelBody>
        <EmptyState>No previous chats yet.</EmptyState>
      </PanelBody>
    );
  }

  // Read once per render rather than per row, so every row in a listing is measured
  // against the same instant.
  const nowSeconds = Math.floor(Date.now() / 1000);

  return (
    <PanelBody>
      <HistoryList data-testid="history-list">
        {conversations.map((conversation) => {
          const isCurrent = conversation.id === activeSessionId;
          const lastActive = formatRelative(conversation.lastActiveAt, nowSeconds);
          return (
            <HistoryRow
              $active={isCurrent}
              key={conversation.id}
              onClick={() => onSelect(conversation.id)}
              type="button"
            >
              <HistoryRowTop>
                <HistoryPreview>{conversation.preview || 'Untitled conversation'}</HistoryPreview>
                {isCurrent && <CurrentBadge>Current</CurrentBadge>}
              </HistoryRowTop>
              {lastActive && <HistoryMeta>Last active {lastActive}</HistoryMeta>}
            </HistoryRow>
          );
        })}
        {nextCursor && (
          <LoadMoreButton disabled={isLoadingMore} onClick={loadMore} type="button">
            {isLoadingMore ? 'Loading…' : 'Load older chats'}
          </LoadMoreButton>
        )}
        {loadMoreError && <LoadMoreError>Could not load more chats. {loadMoreError}</LoadMoreError>}
      </HistoryList>
    </PanelBody>
  );
}
